import React, {FC} from 'react';
import {graphql} from 'gatsby';
import styled from 'styled-components';

import {GlobalStateContext, SiteStyles} from '../components/common/layout';
import {MetaTags} from '../components/common/meta-tags';
import {TopBarR4} from '../components/molecules/topbarR4';
import {Header as Navigation} from '../templates/contentful-model/header';
import {Footer} from '../templates/contentful-model/footer';
import { fontFamiliesR4, paletteR4 } from '../lib/styleVariablesR4';
import { PrimaryButton } from '../components/molecules/buttonsR4';

const PageWrapper = styled.div`
  overflow: hidden;
  font-family: ${fontFamiliesR4.body};
  font-feature-settings: 'titl' on, 'salt' on;
  padding-bottom: 80px;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  min-width: 126px;
  display: inline-flex;
`;

const ButtonWrapper = styled.div<{ forceCenter?: boolean }>`
  text-align: ${props => props.forceCenter ? 'center' : 'left'};

  @media (min-width: 768px) {
    text-align: center;
  }
`;

const Container = styled.div`
  padding: 0 32px;
  margin: 0 auto;
  max-width: 1200px;
  box-sizing: content-box;

  @media (min-width: 768px) {
    padding: 0 48px;
  }
`;

const Hero = styled.header`
  background-image: url('https://cdn.front.com/staticwebassets/pages/crm/hero-bg.jpg');
  background-position: center top;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 0 0 78px 0;

  @media (min-width: 1440px) {
    margin-bottom: 152px;
  }
`;

const HeroWrapper = styled.div`
  padding: 87px 32px 0;
  margin: 0 auto;
  max-width: 1200px;
  box-sizing: content-box;

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;

  @media (min-width: 768px) {
    padding: 60px 48px 0;
    grid-template-columns: 10fr 14fr;
    grid-template-rows: auto;
    align-items: flex-end;
  }

  @media (min-width: 1440px) {
    padding: 115px 48px 0;
  }
`;

const HeroLeftColumn = styled.div`
  grid-row: 2;

  @media (min-width: 768px) {
    grid-row: 1;
    grid-column: 1;
  }
`;

const HeroRightColumn = styled.div`
  grid-row: 1;

  @media (min-width: 768px) {
    grid-column: 2;
    padding-bottom: 60px;
  }

  @media (min-width: 1440px) {
    padding-bottom: 115px;
  }
`;

const HeroHeadline = styled.h1`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  span {
    visibility: hidden;
    position: absolute;
  }

  img {
    margin: 0;
  }
`;

const HeroHeadlineLarge = styled.img`
  display: none;
  width: 607px;

  @media (min-width: 1440px) {
    display: inline-block;
  }
`;

const HeroHeadlineMedium = styled.img`
  display: none;
  width: 391px;

  @media (min-width: 768px) {
    display: inline-block;
  }

  @media (min-width: 1440px) {
    display: none;
  }
`;

const HeroHeadlineSmall = styled.img`
  display: inline-block;
  width: 262px;

  @media (min-width: 768px) {
    display: none;
  }
`;

const HeroParagraph = styled.p`
  font-size: 18px;
  line-height: 25px;
  color: ${paletteR4.black.base};
  margin: 40px auto;
  width: 85%;
  text-align: center;

  @media (min-width: 768px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  @media (min-width: 1440px) {
    font-size: 24px;
    line-height: 34px;
    margin: 40px auto;
  }
`;

const HeroIllustration = styled.img`
  width: 100%;
  max-width: 335px;
  margin: 40px auto 0;
  display: block;

  @media (min-width: 768px) {
    margin-top: 0;
    max-width: 285px;
  }

  @media (min-width: 1440px) {
    max-width: 537px;
  }
`;

interface FeatureGridProps {
  reversed?: boolean;
}

const FeatureGridImageCell = styled.div`
  grid-row: 2;

  @media (min-width: 768px) {
    grid-row: 1;
  }

  img,
  video {
    width: 100%;
  }
`;

const FeatureGridContentCell = styled.div`
  grid-row: 1;

  @media (min-width: 768px) {
    grid-column: 1 / span 11;
  }

  @media (min-width: 1440px) {
    padding: 0 65px;
  }
`;

const FeatureGrid = styled.div<FeatureGridProps>`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  margin: 0 0 85px 0;
  gap: 40px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-template-rows: auto;
    align-items: center;
    gap: 0;

    margin-bottom: 65px;

    ${FeatureGridContentCell} {
      grid-column: ${props => props.reversed ? 13 : 1} / span 12;
    }

    ${FeatureGridImageCell} {
      grid-column: ${props => props.reversed ? 1 : 14} / span 11;
    }
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(14, minmax(0, 1fr));

    ${FeatureGridContentCell} {
      grid-column: ${props => props.reversed ? 1 : 10} / span 5;
    }

    ${FeatureGridImageCell} {
      grid-column: ${props => props.reversed ? 6 : 1} / span 9;
    }
  }
`;

interface FeatureTitleProps {
  textColor: string;
}

const FeatureTitle = styled.h2<FeatureTitleProps>`
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  color: ${props => props.textColor};
  margin: 0 0 14px 0;
`;

const FeatureParagraph = styled.p`
  font-size: 16px;
  line-height: 22px;
  margin: 0;
`;

interface HeadlineProps {
  textColor?: string;
}

const Headline = styled.h3<HeadlineProps>`
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  text-align: center;
  color: ${props => props.textColor ?? paletteR4.black.base};

  @media (min-width: 1440px) {
    font-size: 42px;
    line-height: 53px;
    letter-spacing: -0.01em;
  }
`;

const IntegrationsGrid = styled.div`
  ${Headline} {
    padding: 0 30px;
    margin: 0 0 40px 0;
  }

  img {
    display: block;
    width: 100%;
    max-width: 380px;
    margin: 0 auto;
  }

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-template-rows: auto;
    align-items: center;

    ${Headline} {
      grid-row: 1;
      grid-column: 16 / span 6;
      padding: 0;
      margin: -100px 0 0 0;
    }

    img {
      grid-row: 1;
      grid-column: 3 / span 12;
      max-width: none;
    }
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(14, minmax(0, 1fr));

    img {
      grid-column: 1 / span 7;
    }

    ${Headline} {
      grid-column: 9 / span 5;
      margin: 0;
    }
  }
`;

const FooterGrid = styled.div`
  margin-top: 117px;

  ${Headline} {
    padding: 0 30px;
    margin: 0 0 32px 0;
  }

  ${ButtonWrapper} {
    margin: 0 0 117px 0;
  }

  img {
    display: block;
    width: 100%;
    max-width: 380px;
    margin: 40px auto 0;
  }

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-template-rows: auto;
    align-items: center;

    div {
      grid-row: 1;
      grid-column: 3 / span 9;
    }

    ${Headline} {
      padding: 0;
    }

    ${ButtonWrapper} {
      margin-bottom: 0;
    }

    img {
      grid-row: 1;
      grid-column: 13 / span 11;
      max-width: none;
      margin-top: -150px;
    }
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(14, minmax(0, 1fr));

    img {
      grid-column: 8 / span 7;
    }

    div {
      grid-column: 2 / span 5;
    }
  }
`;

const Page: FC = (props: any) => {
  const { header, footer } = props.data;

  return (
    <>
      <MetaTags
        lang="en-US"
        title="Email in the Front, Your CRM in the Back | Front"
        description="Sometimes when you blend two things, you make one amazing thing. Front is like that—learn how we combined your CRM &amp; inbox all in one hub for your customers."
        keywords={[]}
        ogImage="https://cdn.front.com/staticwebassets/pages/crm/social-share-image.png"
      />
      <TopBarR4 />
      <Navigation data={header} locationPath={props.path} hasBackgroundColorAnimation />
      <SiteStyles>
        <GlobalStateContext.Consumer>
          {globalState => (
            <PageWrapper>
              <Hero>
                <HeroWrapper>
                  <HeroLeftColumn>
                    <HeroIllustration src="https://cdn.front.com/staticwebassets/pages/crm/hero-illustration.png" />
                  </HeroLeftColumn>
                  <HeroRightColumn>
                    <HeroHeadline>
                      <HeroHeadlineLarge src="https://cdn.front.com/staticwebassets/pages/crm/hero-text-large.png" aria-hidden alt="Business in the Front, party in the back" />
                      <HeroHeadlineMedium src="https://cdn.front.com/staticwebassets/pages/crm/hero-text-medium.png" aria-hidden alt="Business in the Front, party in the back" />
                      <HeroHeadlineSmall src="https://cdn.front.com/staticwebassets/pages/crm/hero-text-small.png" aria-hidden alt="Business in the Front, party in the back" />
                      <span>Business in the Front, party in the back</span>
                    </HeroHeadline>

                    <HeroParagraph>
                      Sometimes when you blend two things, you make one amazing thing. We did that with email and your CRM.
                    </HeroParagraph>

                    <ButtonWrapper forceCenter>
                      <StyledPrimaryButton componentType="button" onClick={() => globalState.toggleGetStartedModal(true)}>​​
                        Get Started
                      </StyledPrimaryButton>
                    </ButtonWrapper>
                  </HeroRightColumn>
                </HeroWrapper>
              </Hero>

              <Container>
                <FeatureGrid>
                  <FeatureGridContentCell>
                    <FeatureTitle textColor={paletteR4.red.lowlight!}>
                      Need details on your customer? It’s all here.
                    </FeatureTitle>
                    <FeatureParagraph>
                      Customer data, messages across every channel, and complete conversation history—it’s all in Front.
                    </FeatureParagraph>
                  </FeatureGridContentCell>
                  <FeatureGridImageCell>
                    <video autoPlay muted playsInline loop={false} controls={false}>
                      <source src="https://cdn.front.com/assets/pages/teams/feature-contacts-animated.mp4" />
                    </video>
                  </FeatureGridImageCell>
                </FeatureGrid>

                <FeatureGrid reversed>
                  <FeatureGridContentCell>
                    <FeatureTitle textColor={paletteR4.blue.lowlight!}>
                      Workflows that feel effortless
                    </FeatureTitle>
                    <FeatureParagraph>
                      Create custom workflows that automatically route, triage, and escalate messages—and give you time back. All your customers see is a thorough, swift response.
                    </FeatureParagraph>
                  </FeatureGridContentCell>
                  <FeatureGridImageCell>
                    <video autoPlay muted playsInline loop={false} controls={false}>
                      <source src="https://cdn.front.com/assets/pages/teams/feature-rules-animated.mp4" />
                    </video>
                  </FeatureGridImageCell>
                </FeatureGrid>

                <FeatureGrid>
                  <FeatureGridContentCell>
                    <FeatureTitle textColor={paletteR4.green.lowlight!}>
                      Work together like never before
                    </FeatureTitle>
                    <FeatureParagraph>
                      Collaborate on email threads internally, so you can chat through tricky asks and get the full picture of every situation before hitting send.
                    </FeatureParagraph>
                  </FeatureGridContentCell>
                  <FeatureGridImageCell>
                    <video autoPlay muted playsInline loop={false} controls={false}>
                      <source src="https://cdn.front.com/assets/pages/teams/feature-comments-animated.mp4" />
                    </video>
                  </FeatureGridImageCell>
                </FeatureGrid>

                <FeatureGrid reversed>
                  <FeatureGridContentCell>
                    <FeatureTitle textColor={paletteR4.orange.lowlight!}>
                      Insights for an unforgettable customer experience
                    </FeatureTitle>
                    <FeatureParagraph>
                      When personal details, deep context, and critical metrics come together, the result is an experience your customers will love.
                    </FeatureParagraph>
                  </FeatureGridContentCell>
                  <FeatureGridImageCell>
                    <video autoPlay muted playsInline loop={false} controls={false}>
                      <source src="https://cdn.front.com/assets/pages/teams/feature-analytics-animated.mp4" />
                    </video>
                  </FeatureGridImageCell>
                </FeatureGrid>

                <IntegrationsGrid>
                  <Headline textColor={paletteR4.green.lowlight}>
                    Connect all your apps to your inbox
                  </Headline>
                  <img src="https://cdn.front.com/staticwebassets/pages/crm/integrations.png" />
                </IntegrationsGrid>

                <FooterGrid>
                  <div>
                    <Headline textColor={paletteR4.red.lowlight}>
                      Get the best of both worlds with Front
                    </Headline>
                    <ButtonWrapper forceCenter>
                      <StyledPrimaryButton componentType="a" href="/why-front">​​Learn more about Front</StyledPrimaryButton>
                    </ButtonWrapper>
                  </div>
                  <img src="https://cdn.front.com/staticwebassets/pages/crm/footer-illustration.png" />
                </FooterGrid>
              </Container>
            </PageWrapper>
          )}
        </GlobalStateContext.Consumer>
      </SiteStyles>
      <Footer data={footer} />
    </>
  );
};

export default Page;

export const query = graphql`
  query CRMPage {
    header: contentfulCommonJsonData(contentful_id: {eq: "7tW6ihv7EKcLJXbYJt5PGV"}) {
      ...HeaderMenu
    }
    footer: contentfulCommonJsonData(contentful_id: {eq: "5xq6df3Rlh9HD8O4SnJsdi"}) {
      ...Footer
    }
  }
`;
